import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, flatMap, map, takeUntil, tap } from 'rxjs/operators';
import { GroupsService } from '../services/groups.service';

@Component({
    selector: 'user-edit-groups',
    styles: [`
        .scrollPanel {
            overflow-y: auto;
            height: calc(100vh - 130px);
        }
        .cdk-column-description.mat-column-description {
            width: calc(100vw - 200px);
        }
        .cdk-column-members.mat-column-members {
            width: 85px;
            text-align: center;
        }
    `],
    template: `
        <mat-toolbar>
            <mat-form-field style="font-size: 16px; font-weight: 400;">
                <input matInput placeholder="Filter" [formControl]="filterControl">
            </mat-form-field>
        </mat-toolbar>
        <mat-card>
            <mat-card-content>
                <div class="scrollPanel"
                     infiniteScroll 
                     [infiniteScrollThrottle]="300"
                     [infiniteScrollDistance]="1"
                     [scrollWindow]="false"
                     (scrolled)="nextPage()">
                    <table mat-table
                           matSort 
                           (matSortChange)="sort($event)"
                           [matSortDisableClear]="true "
                           [matSortDirection]="params?.value?.sortDirection" 
                           [matSortActive]="params?.value?.sort" 
                           *ngIf="!noTouching" 
                           [dataSource]="groups" 
                           style="width: 100%;">
                        <ng-container matColumnDef="name">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="nameLower" width="20%">Name</th>
                            <td mat-cell *matCellDef="let row">
                                <a [routerLink]="['/settings', 'groups', row.id]">{{row.name}}</a>
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="description">
                            <th mat-header-cell *matHeaderCellDef>Description</th>
                            <td mat-cell *matCellDef="let row">{{row.description}}</td>
                        </ng-container>
                        <ng-container matColumnDef="members">
                            <th mat-header-cell *matHeaderCellDef mat-sort-header="membersCount" start="desc">Members</th>
                            <td mat-cell *matCellDef="let row">{{row.membersCount}}</td>
                        </ng-container>
                        <ng-container matColumnDef="action">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let row">
                                <button mat-button *ngIf="row.userIsMember" matTooltip="Remove" (click)="toggleMembership(row)">Remove</button>
                                <button mat-button *ngIf="!row.userIsMember" color="primary" matTooltip="Add" (click)="toggleMembership(row)" color="primary">Add</button>
                            </td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="['name', 'description', 'members', 'action']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['name', 'description', 'members', 'action'];"></tr>
                    </table>
                    <div *ngIf="noTouchingPaging" fxLayoutAlign="center center"><spinner></spinner></div>
                </div>
            </mat-card-content>
        </mat-card>
    `
})
export class UserEditGroupsComponent implements OnChanges, OnDestroy {
    private cleanupTime = new Subject<void>();
    @Input() userId: string;
    filterControl = new FormControl();
    noTouching = true;
    params = new BehaviorSubject<any>({ 
        sort: 'nameLower',
        sortDirection: 'asc',
        page: 0, 
        rows: 40
    });
    groups = new BehaviorSubject<any[]>([]);
    noTouchingPaging: boolean;
    lastFetched: any;
    
    constructor(private groupsService: GroupsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.userId.firstChange) {
            this.params
            .pipe(
                filter(() => !!this.userId),
                flatMap(params => this.groupsService.getUserGroups(Object.assign({ targetUserId: this.userId }, params))),
                tap(() => this.noTouching = false),
                tap(() => this.noTouchingPaging = false),
                tap(res => this.lastFetched = res.last),
                map(res => res.content),
                tap(content => content.forEach(group => group.userIsMember = true)),
                takeUntil(this.cleanupTime)
            )
            .subscribe(content => this.groups.next(this.groups.value.concat(content)));

            this.filterControl.valueChanges
            .pipe(
                debounceTime(500),
                tap(() => this.groups.next([])),
                takeUntil(this.cleanupTime)
            ).subscribe(term => this.params.next(Object.assign({}, this.params.value, { term, page: 0 })));
        }
    }

    sort(sort: Sort): void {
        const params = this.params.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.noTouchingPaging = true;
        this.groups.next([]);
        this.params.next(params);
    }

    nextPage(): void {
        const params = this.params.value;
        params.page = params.page + 1;
        this.noTouchingPaging = true;
        this.params.next(params);
    }

    toggleMembership(group): void {
        const obs = group.userIsMember ? 
            this.groupsService.removeUser(group.id, this.userId) :
            this.groupsService.addUser(group.id, this.userId);

        obs.pipe(
            tap(() => group.userIsMember ? group.membersCount-- : group.membersCount++),
            tap(() => group.userIsMember = !group.userIsMember)
        ).subscribe();
    }

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}